import { formatDistanceToNowStrict } from "date-fns";

import { misc, neutral, primary, success } from "app/themes/colors";
import type {
  CallContact,
  ContactKind,
  ContactStaffUser
} from "services/dialstack";

export const CONTACT_KINDS: ContactKind[] = [
  "insurer",
  "vendor",
  "referral_partner",
  "pharmacy",
  "family",
  "other"
];

export const CONTACT_KIND_LABELS: Record<ContactKind, string> = {
  insurer: "Insurer",
  vendor: "Vendor",
  referral_partner: "Referral partner",
  pharmacy: "Pharmacy",
  family: "Family",
  other: "Other"
};

// Plural, for the Rolodex filter chips.
export const CONTACT_KIND_PLURAL_LABELS: Record<ContactKind, string> = {
  insurer: "Insurers",
  vendor: "Vendors",
  referral_partner: "Referral partners",
  pharmacy: "Pharmacies",
  family: "Family",
  other: "Other"
};

export function contactKindLabel(kind: ContactKind | undefined) {
  return CONTACT_KIND_LABELS[kind ?? "other"];
}

// The avatar tint carries the kind onto every row and card, so an insurer
// and a relative read differently at a glance. Palette tokens only; every
// pair clears 4.5:1 (misc.warningText on lightYellow was 3.7:1, hence the
// theme's warning.dark for family).
export const CONTACT_KIND_TINTS: Record<
  ContactKind,
  { background: string; color: string }
> = {
  insurer: { background: misc.lightGreen, color: primary[500] },
  vendor: { background: misc.lightLavender, color: misc.purpleText },
  referral_partner: { background: misc.mutedPurple, color: misc.grayAsphalt },
  pharmacy: { background: success[100], color: success[800] },
  family: { background: misc.lightYellow, color: "warning.dark" },
  other: { background: neutral[100], color: neutral[800] }
};

export function contactKindTint(kind: ContactKind | undefined) {
  return CONTACT_KIND_TINTS[kind ?? "other"];
}

// Up to two initials from the name ("Acme Insurance" → "AI", "Bob" → "B").
export function contactInitials(name: string) {
  const words = name.trim().split(/\s+/).filter(Boolean);
  if (words.length === 0) return "?";
  const first = words[0].charAt(0);
  const last = words.length > 1 ? words[words.length - 1].charAt(0) : "";
  return `${first}${last}`.toUpperCase();
}

// The A–Z group a contact files under; anything that is not a letter is "#".
export function contactGroupLetter(name: string) {
  const letter = name.trim().charAt(0).toUpperCase();
  return /^[A-Z]$/.test(letter) ? letter : "#";
}

export const ALPHABET_RAIL = ["#", ..."ABCDEFGHIJKLMNOPQRSTUVWXYZ".split("")];

export function contactPath(contactId: number) {
  return `/calls/rolodex?contact=${contactId}`;
}

export function staffUserName(user: ContactStaffUser | null | undefined) {
  if (!user) return null;
  return `${user.first_name} ${user.last_name}`.trim() || null;
}

// "3 days ago" / "2 hours ago"; null when there is no date.
export function relativeTime(iso: string | null | undefined) {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;
  return formatDistanceToNowStrict(date, { addSuffix: true });
}

// Second line under a contact's name: always the kind in words (the avatar
// tint alone must not carry it), then the organization when it has one —
// "Insurer · Acme", "Family".
export function contactSubtitle(contact: CallContact) {
  return [contactKindLabel(contact.kind), contact.organization]
    .filter(Boolean)
    .join(" · ");
}
